import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Upload, ExternalLink } from "lucide-react";
import { API } from "@/lib/auth";

const SECTION_LABELS = {
  hero: "Hero", intro: "Introduction", experience: "Your Bridal Experience", menu: "Bridal Hair Menu",
  extensions: "Bridal Extensions", morning: "Your Wedding Morning", mobile: "Mobile Bridal Bookings",
  gallery: "Gallery heading", faqs: "FAQs", cta: "Final call to action", form: "Enquiry form & success message",
};
const LONG = /(subtitle|intro|body|^a$|description|success_body|booking_note)/;
const nice = (k) => k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const inputCls = "w-full rounded-xl border border-[hsl(var(--onyx)/0.15)] bg-white px-3 py-2 text-sm";

function ImageField({ value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/upload/bridal`, fd);
      onChange(r.data.url); toast.success("Image uploaded — remember to save");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-20 rounded-xl bg-[hsl(var(--blush)/0.4)] overflow-hidden shrink-0 flex items-center justify-center text-[10px] text-center text-[hsl(var(--onyx)/0.5)]">
        {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : "Placeholder shown"}
      </div>
      <div className="flex flex-wrap gap-2">
        <label className="text-xs px-3 py-2 rounded-full border cursor-pointer inline-flex items-center gap-1.5" data-testid={`${testId}-upload`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload photo
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        {value && <button type="button" onClick={() => onChange("")} data-testid={`${testId}-clear`} className="text-xs px-3 py-2 rounded-full border text-red-600">Remove</button>}
      </div>
    </div>
  );
}

const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

const RowTools = ({ arr, i, onChange, testId }) => (
  <div className="flex gap-1 shrink-0">
    <button type="button" aria-label="Move up" onClick={() => onChange(move(arr, i, -1))} className="p-1.5 rounded-full border"><ArrowUp size={11} /></button>
    <button type="button" aria-label="Move down" onClick={() => onChange(move(arr, i, 1))} className="p-1.5 rounded-full border"><ArrowDown size={11} /></button>
    <button type="button" aria-label="Remove" data-testid={`${testId}-remove-${i}`} onClick={() => onChange(arr.filter((_, j) => j !== i))} className="p-1.5 rounded-full border text-red-600"><Trash2 size={11} /></button>
  </div>
);

function ScalarValue({ k, value, onChange, testId }) {
  if (k.endsWith("image_url")) return <ImageField value={value} onChange={onChange} testId={testId} />;
  if (typeof value === "number") return <input type="number" data-testid={testId} className={`${inputCls} max-w-[120px]`} value={value} onChange={(e) => onChange(Number(e.target.value))} />;
  const long = LONG.test(k) || (value || "").length > 90;
  return long
    ? <textarea data-testid={testId} rows={3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    : <input data-testid={testId} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

const itemLabel = (ik) => (ik === "q" ? "Question" : ik === "a" ? "Answer" : nice(ik));

function ListValue({ k, value, onChange, testId }) {
  const isObj = value.length > 0 && typeof value[0] === "object";
  const blank = isObj ? Object.fromEntries(Object.keys(value[0]).map((x) => [x, ""])) : "";
  const setItem = (i, v) => onChange(value.map((x, j) => (j === i ? v : x)));
  return (
    <div className="space-y-2">
      {value.map((item, i) => (
        <div key={i} className="flex gap-2 items-start rounded-2xl bg-[hsl(var(--blush)/0.2)] p-2">
          <div className="flex-1 space-y-2">
            {isObj ? Object.entries(item).map(([ik, iv]) => (
              <div key={ik}>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--onyx)/0.5)] mb-1">{itemLabel(ik)}</div>
                <ScalarValue k={ik} value={iv} testId={`${testId}-${i}-${ik}`} onChange={(v) => setItem(i, { ...item, [ik]: v })} />
              </div>
            )) : <ScalarValue k={k} value={item} testId={`${testId}-${i}`} onChange={(v) => setItem(i, v)} />}
          </div>
          <RowTools arr={value} i={i} onChange={onChange} testId={testId} />
        </div>
      ))}
      <button type="button" data-testid={`${testId}-add`} onClick={() => onChange([...value, blank])} className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1"><Plus size={12} /> Add</button>
    </div>
  );
}

const Value = (props) => (Array.isArray(props.value) ? <ListValue {...props} /> : <ScalarValue {...props} />);

function SectionCard({ name, data, onSaved }) {
  const [draft, setDraft] = useState(data);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(data), [data]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(data);
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/bridal/content`, { [name]: draft });
      onSaved(r.data); toast.success(`${SECTION_LABELS[name]} saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <details data-testid={`bridal-content-section-${name}`} className="group rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
      <summary className="cursor-pointer flex items-center justify-between font-display text-xl list-none">
        {SECTION_LABELS[name] || nice(name)}
        {dirty && <span className="text-[10px] uppercase tracking-wider text-[#8B1B3A]">Unsaved</span>}
      </summary>
      <div className="mt-5 space-y-4">
        {Object.entries(draft).map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--onyx)/0.55)] mb-1.5">{nice(k)}</div>
            <Value k={k} value={v} testId={`bridal-content-${name}-${k}`} onChange={(nv) => setDraft((d) => ({ ...d, [k]: nv }))} />
          </div>
        ))}
        <button onClick={save} disabled={!dirty || saving} data-testid={`bridal-content-save-${name}`} className="btn-pill btn-primary disabled:opacity-50">
          {saving ? "Saving…" : "Save section"}
        </button>
      </div>
    </details>
  );
}

export default function BridalContentEditor() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/bridal/content`).then((r) => setContent(r.data)).catch(() => toast.error("Could not load bridal content"));
  }, []);
  if (!content) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;
  return (
    <div data-testid="bridal-content-editor" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[hsl(var(--onyx)/0.6)]">Edit any wording, prices, FAQs or photos. Each section saves separately.</p>
        <a href="/bridal" target="_blank" rel="noreferrer" data-testid="bridal-view-page-link" className="text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1.5"><ExternalLink size={12} /> View page</a>
      </div>
      {Object.keys(SECTION_LABELS).map((k) => content[k] && <SectionCard key={k} name={k} data={content[k]} onSaved={setContent} />)}
    </div>
  );
}
